import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { EnrichedMetadata } from "../../Core/Api/Types/enriched-metadata";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { BundleMetadataValidationStatus } from "../../Utils/Metadata/utils-metadata-validation";
import { useSelector } from "react-redux";
import {
  GetFieldUpdate,
  GetPackageBroadcasterId,
  GetSelectedCulture,
} from "../../Redux/Reducers/redux-slice-bundle-maker";
import { useDispatch } from "react-redux";
import UploadIcon from "@mui/icons-material/Upload";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { ModalContext } from "../../Components/Modals";
import ModalBundlemakerUploadArtwork, {
  ModalBundlemakerUploadArtworkProps,
} from "../../Components/Modals/ModalBundlemakerUploadArtwork/modal-bundlemaker-upload-artwork";
import { thumbnailService } from "../../Core/Api/services";
import ArtworkCard from "./artwork-card";
import Viewer from "react-viewer";
import { ThumbnailSize } from "../../Core/Api/Thumbnail/thumbnail-api-service";
import ModalBundlemakerAddArtwork, {
  ModalAddArtworkEditProps,
} from "../../Components/Modals/ModalBundlemakerAddArworkToCulture/modal-bundlemaker-add-artwork-to-culture";
import FieldInputBundlemaker from "./field-input-bundlemaker";
import { useTranslation } from "react-i18next";

type generativeFormBundleMakerProps = {
  currentMetadataValidation;
  packageId: number;
  synoMeta: EnrichedMetadata[];
  castMeta: EnrichedMetadata[];
  otherMeta: EnrichedMetadata[];
  commonMeta: EnrichedMetadata[];
  selectedCultureArtwork: any;
  epNb: number | null;
};

const IMG_WIDTH = [50, 100, 150, 200, 250, 300];

const GenerativeFormBundleMaker: React.FC<generativeFormBundleMakerProps> = (props: generativeFormBundleMakerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const {
    currentMetadataValidation,
    packageId,
    castMeta,
    commonMeta,
    otherMeta,
    synoMeta,
    selectedCultureArtwork,
    epNb,
  } = props;

  //------Select store values------

  const selectedCulture = useSelector(GetSelectedCulture);
  const updateField = useSelector(GetFieldUpdate);
  const selectedBroadcasterId = useSelector(GetPackageBroadcasterId);
  //------local state------

  const [synoOpen, setSynoOpen] = useState(true);
  const [castOpen, setCastOpen] = useState(true);
  const [otherOpen, setOtherOpen] = useState(true);
  const [commonOpen, setCommonOpen] = useState(true);
  const [artworkOpen, setArtworkOpen] = useState(true);
  const [posterViewerVisible, setPosterViewerVisible] = useState<number | undefined>(undefined);
  const [zoom, setZoom] = useState(3);

  //------handle user actions------

  const handleOpenArtworkModal = useCallback(
    (e) => {
      e.stopPropagation();
      openModal &&
        openModal<ModalBundlemakerUploadArtworkProps>(ModalBundlemakerUploadArtwork, {
          closeModal: () => {},
          cultureId: selectedCulture?.id || null,
          languageId: (selectedCulture?.languagesMap && selectedCulture?.languagesMap[0].id) || null,
          broadcasterId: selectedBroadcasterId || null,
        });
    },
    [selectedCulture?.id]
  );

  const handleOpenAddArtworkModal = useCallback(
    (e) => {
      e.stopPropagation();
      openModal &&
        openModal<ModalAddArtworkEditProps>(ModalBundlemakerAddArtwork, {
          closeModal: () => {},
          cultureId: selectedCulture?.id || null,
          onClose: () => {},
        });
    },
    [selectedCulture?.id]
  );

  const handleZoom = (zoomIn: boolean, e) => {
    e.stopPropagation();
    if (zoomIn && zoom < 5) setZoom((x) => x + 1);
    if (!zoomIn && zoom > 0) setZoom((x) => x - 1);
  };

  const synoMemo = useMemo(
    () => (
      <Accordion expanded={synoOpen} onChange={() => setSynoOpen((state) => !state)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t("common:synopses")}</AccordionSummary>
        <AccordionDetails sx={{ display: "flex" }}>
          {synoMeta.map((m, index) => (
            <FieldInputBundlemaker
              epNb={epNb}
              key={index}
              index={index}
              metaGroup={"syno"}
              triggerChange={
                updateField.field === `syno_${index}` || updateField.field === "all" ? updateField.count : 0
              }
              m={m}
              packageId={packageId}
              validationStatus={
                currentMetadataValidation.find((x) => x.fieldId === m.field?.id)?.status ===
                BundleMetadataValidationStatus.Ok
                  ? "complete"
                  : m.usage?.isMandatory
                  ? "warning"
                  : "todo"
              }
            />
          ))}
        </AccordionDetails>
      </Accordion>
    ),
    [synoOpen, synoMeta]
  );

  const castMemo = useMemo(
    () => (
      <Accordion expanded={castOpen} onChange={() => setCastOpen((state) => !state)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>Casts</AccordionSummary>
        <AccordionDetails>
          {castMeta.map((m, index) => (
            <FieldInputBundlemaker
              epNb={epNb}
              key={index}
              metaGroup={"cast"}
              index={index}
              triggerChange={updateField.field === "cast" || updateField.field === "all" ? updateField.count : 0}
              m={m}
              packageId={packageId}
              validationStatus={
                currentMetadataValidation.find((x) => x.fieldId === m.field?.id)?.status ===
                BundleMetadataValidationStatus.Ok
                  ? "complete"
                  : m.usage?.isMandatory
                  ? "warning"
                  : "todo"
              }
            />
          ))}
        </AccordionDetails>
      </Accordion>
    ),
    [castOpen, castMeta]
  );

  const otherMemo = useMemo(
    () => (
      <Accordion expanded={otherOpen} onChange={() => setOtherOpen((state) => !state)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t("common:others")}</AccordionSummary>
        <AccordionDetails sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
          {otherMeta.map((m, index) => (
            <>
              {selectedCulture &&
                !(
                  m.field?.baseType === BaseMetadataType.DbLocalizedRatingMultiMetadataValueInfos &&
                  !m.value?.value.find((x) => x.system.cultureId === selectedCulture.id)
                ) && (
                  <Box key={index} width="49%" marginBottom="20px">
                    <FieldInputBundlemaker
                      epNb={epNb}
                      metaGroup={"other"}
                      triggerChange={
                        updateField.field === `other_${index}` || updateField.field === "all" ? updateField.count : 0
                      }
                      index={index}
                      m={m}
                      packageId={packageId}
                      validationStatus={
                        currentMetadataValidation.find((x) => x.fieldId === m.field?.id)?.status ===
                        BundleMetadataValidationStatus.Ok
                          ? "complete"
                          : m.usage?.isMandatory
                          ? "warning"
                          : "todo"
                      }
                    />
                  </Box>
                )}
            </>
          ))}
        </AccordionDetails>
      </Accordion>
    ),
    [otherOpen, otherMeta]
  );

  const commonMemo = useMemo(
    () => (
      <Accordion expanded={commonOpen} onChange={() => setCommonOpen((state) => !state)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t("common:common")}</AccordionSummary>
        <AccordionDetails sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
          {commonMeta.map((m, index) => (
            <Box key={index} width="49%" marginBottom="20px">
              <FieldInputBundlemaker
                index={index}
                metaGroup={"common"}
                m={m}
                triggerChange={
                  updateField.field === `common_${index}` || updateField.field === "all" ? updateField.count : 0
                }
                packageId={packageId}
                validationStatus={
                  currentMetadataValidation.find((x) => x.fieldId === m.field?.id)?.status ===
                  BundleMetadataValidationStatus.Ok
                    ? "complete"
                    : m.usage?.isMandatory
                    ? "warning"
                    : "todo"
                }
              />
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    ),
    [commonOpen, commonMeta]
  );

  const artworkMemo = useMemo(
    () => (
      <Accordion expanded={artworkOpen} onChange={() => setArtworkOpen((state) => !state)}>
        <AccordionSummary sx={{ position: "relative" }} expandIcon={<ExpandMoreIcon />}>
          <Typography>{t("page:body.artworks")}</Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" width="100%" marginRight="20px">
            <Tooltip title={t("tooltip:title.manageAssociatedArtworks")}>
              <IconButton onClick={(e) => handleOpenAddArtworkModal(e)}>
                <AddPhotoAlternateIcon sx={{ width: "24px", height: "24px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("tooltip:title.uploadArtworks")}>
              <IconButton onClick={(e) => handleOpenArtworkModal(e)}>
                <UploadIcon sx={{ width: "24px", height: "24px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("tooltip:title.zoomOut")}>
              <IconButton disabled={zoom < 1} onClick={(e) => handleZoom(false, e)}>
                <ZoomOutIcon sx={{ width: "24px", height: "24px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("tooltip:title.zoomIn")}>
              <IconButton disabled={zoom >= IMG_WIDTH.length - 1} onClick={(e) => handleZoom(true, e)}>
                <ZoomInIcon sx={{ width: "24px", height: "24px" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ justifyContent: "flex-start", display: "flex", flexWrap: "wrap" }}>
          {selectedCultureArtwork.map((m, index) => (
            <ArtworkCard
              key={index}
              imgWidth={IMG_WIDTH[zoom]}
              setPosterViewerVisible={setPosterViewerVisible}
              data={m}
              index={index}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    ),
    [artworkOpen, selectedCultureArtwork, zoom]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflowY: "scroll",
        height: "100%",
        paddingLeft: "24px",
        marginTop: "-12px",
      }}
    >
      {synoMeta && synoMeta.length > 0 && synoMemo}
      {castMeta.length > 0 && castMemo}
      {otherMeta.length > 0 && otherMemo}
      {commonMeta.length > 0 && commonMemo}

      {artworkMemo}
      <Viewer
        visible={posterViewerVisible !== undefined}
        noNavbar={true}
        zIndex={2001}
        onClose={() => {
          setPosterViewerVisible(undefined);
        }}
        images={[
          {
            src: thumbnailService.GetObjectPosterUrl(posterViewerVisible, ThumbnailSize.original),
            alt: "Image viewer #" + posterViewerVisible + " ",
          },
        ]}
      />
    </Box>
  );
};

export default GenerativeFormBundleMaker;
